/**
 * Cache Service
 * 
 * Utilities for querying cached odds and fixtures with automatic fallback to live API
 */

import Constants from 'expo-constants';

// Get functions base URL
const getFunctionsBaseUrl = () => {
  // Check if localhost
  const isLocalhost = typeof window !== 'undefined' 
    && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  
  if (isLocalhost) {
    return 'http://127.0.0.1:5001/bet-optimized-fe922/us-central1'; // Emulator
  }
  
  return (
    (typeof process !== 'undefined' && process?.env?.EXPO_PUBLIC_FUNCTIONS_BASE_URL) ||
    (Constants?.expoConfig?.extra?.EXPO_PUBLIC_FUNCTIONS_BASE_URL) ||
    (Constants?.expoConfig?.extra?.FUNCTIONS_BASE_URL) ||
    (Constants?.manifest?.extra?.FUNCTIONS_BASE_URL) ||
    'https://us-central1-bet-optimized-fe922.cloudfunctions.net' // Production
  );
};

/**
 * Query cached fixtures
 * @param {Object} params - Query parameters
 * @param {string} params.league - League (e.g., 'nba', 'nfl')
 * @param {string} params.sport - Sport (e.g., 'basketball', 'football')
 * @param {string} params.team - Team name
 * @param {string} params.player - Player name
 * @param {string} params.market - Market name
 * @param {number} params.limit - Max results
 * @param {number} params.maxAgeMinutes - Max cache age in minutes
 * @returns {Promise<{success: boolean, cached: boolean, fixtures: Array, message: string}>}
 */
export async function queryCachedFixtures(params = {}) {
  try {
    const {
      league,
      sport,
      team,
      player,
      market,
      limit = 50,
      maxAgeMinutes = 60
    } = params;
    
    const base = getFunctionsBaseUrl();
    const queryParams = new URLSearchParams();
    
    if (league) queryParams.append('league', league);
    if (sport) queryParams.append('sport', sport);
    if (team) queryParams.append('team', team);
    if (player) queryParams.append('player', player);
    if (market) queryParams.append('market', market);
    if (limit) queryParams.append('limit', limit.toString());
    if (maxAgeMinutes) queryParams.append('max_age_minutes', maxAgeMinutes.toString());
    
    const url = `${base}/queryCachedFixtures?${queryParams.toString()}`;
    console.log('[CacheService] Querying cached fixtures:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log('[CacheService] Cache query result:', {
      cached: data.cached,
      fixtures: data.fixtures?.length || 0
    });
    
    return data;
  } catch (error) {
    console.error('[CacheService] Error querying cached fixtures:', error);
    return {
      success: false,
      cached: false,
      fixtures: [],
      message: `Error: ${error.message}`
    };
  }
}

/**
 * Query cached SGPs
 * @param {Object} params - Query parameters
 * @param {string} params.league - League (e.g., 'nba', 'nfl')
 * @param {string} params.sport - Sport (e.g., 'basketball', 'football')
 * @param {string} params.team - Team name
 * @param {string} params.fixture_id - Specific fixture ID
 * @param {number} params.limit - Max results
 * @param {number} params.max_age_hours - Max cache age in hours (default: 6)
 * @param {string} params.sort_by - Sort by field (e.g., 'ev', 'odds')
 * @returns {Promise<{success: boolean, sgps: Array, message: string}>}
 */
export async function queryCachedSGPs(params = {}) {
  try {
    const {
      league,
      sport,
      team,
      fixture_id,
      limit = 50,
      max_age_hours = 6,
      sort_by = 'ev'
    } = params;
    
    const base = getFunctionsBaseUrl();
    const queryParams = new URLSearchParams();
    
    // Only add filters if provided - this allows old SGPs without league/sport to be returned
    if (league) queryParams.append('league', league);
    if (sport) queryParams.append('sport', sport);
    if (team) queryParams.append('team', team);
    if (fixture_id) queryParams.append('fixture_id', fixture_id);
    queryParams.append('limit', limit.toString());
    queryParams.append('max_age_hours', max_age_hours.toString());
    queryParams.append('sort_by', sort_by);
    
    const url = `${base}/queryCachedSGPs?${queryParams.toString()}`;
    console.log('[CacheService] Querying cached SGPs:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log('[CacheService] SGP cache query result:', {
      success: data.success,
      sgps: data.sgps?.length || 0,
      fixtures: data.fixtures?.length || 0,
      totalAvailable: data.total_available
    });
    
    return data;
  } catch (error) {
    console.error('[CacheService] Error querying cached SGPs:', error);
    return {
      success: false,
      sgps: [],
      fixtures: [],
      message: `Error: ${error.message}`
    };
  }
}

/**
 * Get cached odds for a query
 * @param {Object} params - Query parameters
 * @param {string} params.league - League
 * @param {string} params.sport - Sport
 * @param {string} params.team - Team name
 * @param {string} params.player - Player name
 * @param {string} params.market - Market name
 * @param {string} params.date - Date (YYYY-MM-DD)
 * @param {number} params.maxAgeMinutes - Max cache age
 * @returns {Promise<{success: boolean, cached: boolean, odds: Array}>}
 */
export async function getCachedOdds(params = {}) {
  try {
    const {
      league,
      sport,
      team,
      player,
      market,
      date,
      maxAgeMinutes = 60
    } = params;
    
    const base = getFunctionsBaseUrl();
    const url = `${base}/getCachedOdds`;
    
    console.log('[CacheService] Getting cached odds:', params);
    
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        league,
        sport,
        team,
        player,
        market,
        date,
        max_age_minutes: maxAgeMinutes
      })
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log('[CacheService] Cached odds result:', {
      cached: data.cached,
      odds: data.odds?.length || 0
    });
    
    return data;
  } catch (error) {
    console.error('[CacheService] Error getting cached odds:', error);
    return {
      success: false,
      cached: false,
      odds: [],
      message: `Error: ${error.message}`
    };
  }
}

/**
 * Get cache statistics and health
 * @returns {Promise<Object>}
 */
export async function getCacheStats() {
  try {
    const base = getFunctionsBaseUrl();
    const url = `${base}/getCacheStats`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('[CacheService] Error getting cache stats:', error);
    return {
      success: false,
      cache_status: 'error',
      error: error.message
    };
  }
}

/**
 * 🧠 Smart query parser - extracts entities from natural language
 * 
 * NOTE: This is a HELPER for the cache-first flow. The AI (nlOdds) does the heavy lifting
 * for complex parsing. This parser just helps with:
 * 1. Obvious team/market detection (to avoid cache misses)
 * 2. Preventing false positives (e.g., "Lakers Moneyline" as player name)
 * 3. Quick client-side filtering before API calls
 * 
 * The CACHE is the source of truth for:
 * - Available leagues/sports
 * - Available markets
 * - Available odds/SGPs
 * - Team rosters (which league a team is in)
 * 
 * If this parser misses something, the AI will catch it. If the AI misses something,
 * the cache search will fall back gracefully.
 * 
 * @param {string} prompt - Natural language query
 * @returns {Object} - Extracted entities
 */
/**
 * 🚀 OPTIMIZED TEAM LOOKUP SYSTEM
 * 
 * Features:
 * 1. O(1) Map lookups (instead of O(n) array iteration)
 * 2. City-to-teams mapping for location queries
 * 3. Fuzzy matching for typo tolerance
 * 4. Context-aware disambiguation (season, market type)
 * 5. Aliases and nickname support
 * 6. Confidence scoring for ambiguous matches
 */

// Pre-computed lowercase Map for O(1) lookups
const TEAM_LOOKUP_MAP = new Map();

// City-to-teams mapping (one city can have multiple teams)
const CITY_TO_TEAMS = {
  // California
  'los angeles': ['Lakers', 'Clippers', 'Dodgers', 'Angels', 'Rams', 'Chargers', 'Kings', 'Ducks', 'Galaxy', 'LAFC', 'Sparks'],
  'la': ['Lakers', 'Clippers', 'Dodgers', 'Angels', 'Rams', 'Chargers', 'Kings', 'Ducks', 'Galaxy', 'LAFC'],
  'san francisco': ['Giants', '49ers', 'Warriors'],
  'sf': ['Giants', '49ers', 'Warriors'],
  'oakland': ['Athletics', 'Raiders'],
  'san diego': ['Padres', 'Chargers'],
  'sacramento': ['Kings'],
  'anaheim': ['Angels', 'Ducks'],
  
  // New York
  'new york': ['Yankees', 'Mets', 'Knicks', 'Nets', 'Rangers', 'Islanders', 'Giants', 'Jets', 'Liberty', 'NYCFC', 'Red Bulls'],
  'ny': ['Yankees', 'Mets', 'Knicks', 'Nets', 'Rangers', 'Islanders', 'Giants', 'Jets'],
  'nyc': ['Yankees', 'Mets', 'Knicks', 'Nets', 'Rangers', 'Islanders'],
  'brooklyn': ['Nets'],
  'queens': ['Mets'],
  'bronx': ['Yankees'],
  
  // Texas
  'dallas': ['Cowboys', 'Mavericks', 'Stars', 'Rangers', 'FC Dallas', 'Wings'],
  'houston': ['Texans', 'Rockets', 'Astros', 'Dynamo', 'Dash'],
  'san antonio': ['Spurs'],
  'austin': ['Austin FC'],
  
  // Florida
  'miami': ['Heat', 'Dolphins', 'Marlins', 'Panthers', 'Inter Miami'],
  'tampa': ['Buccaneers', 'Lightning', 'Rays'],
  'tampa bay': ['Buccaneers', 'Lightning', 'Rays'],
  'orlando': ['Magic', 'Orlando City'],
  'jacksonville': ['Jaguars'],
  
  // Illinois
  'chicago': ['Bulls', 'Bears', 'Cubs', 'White Sox', 'Blackhawks', 'Sky', 'Fire'],
  
  // Massachusetts
  'boston': ['Celtics', 'Red Sox', 'Patriots', 'Bruins', 'Revolution'],
  
  // Pennsylvania
  'philadelphia': ['76ers', 'Sixers', 'Eagles', 'Phillies', 'Flyers', 'Union'],
  'philly': ['76ers', 'Sixers', 'Eagles', 'Phillies', 'Flyers'],
  'pittsburgh': ['Steelers', 'Pirates', 'Penguins'],
  
  // Ohio
  'cleveland': ['Cavaliers', 'Cavs', 'Browns', 'Guardians'],
  'cincinnati': ['Bengals', 'Reds', 'FC Cincinnati'],
  'columbus': ['Blue Jackets', 'Crew'],
  
  // Michigan
  'detroit': ['Lions', 'Tigers', 'Pistons', 'Red Wings'],
  
  // Georgia
  'atlanta': ['Hawks', 'Falcons', 'Braves', 'Atlanta United', 'Dream'],
  
  // Arizona
  'phoenix': ['Suns', 'Cardinals', 'Diamondbacks', 'Coyotes', 'Mercury'],
  
  // Colorado
  'denver': ['Nuggets', 'Broncos', 'Rockies', 'Avalanche', 'Rapids'],
  
  // Washington
  'seattle': ['Seahawks', 'Mariners', 'Kraken', 'Sounders', 'Storm'],
  
  // Oregon
  'portland': ['Trail Blazers', 'Blazers', 'Timbers'],
  
  // Minnesota
  'minneapolis': ['Timberwolves', 'Vikings', 'Twins', 'Wild', 'Lynx'],
  'minnesota': ['Timberwolves', 'Vikings', 'Twins', 'Wild', 'Lynx'],
  
  // Wisconsin
  'milwaukee': ['Bucks', 'Brewers'],
  'green bay': ['Packers'],
  
  // Indiana
  'indianapolis': ['Colts', 'Pacers', 'Fever'],
  'indy': ['Colts', 'Pacers', 'Fever'],
  
  // Missouri
  'kansas city': ['Chiefs', 'Royals', 'Sporting KC'],
  'kc': ['Chiefs', 'Royals', 'Sporting KC'],
  'st louis': ['Cardinals', 'Blues', 'St. Louis City SC'],
  'st. louis': ['Cardinals', 'Blues', 'St. Louis City SC'],
  
  // Tennessee
  'nashville': ['Titans', 'Predators', 'Nashville SC'],
  'memphis': ['Grizzlies'],
  
  // Louisiana
  'new orleans': ['Saints', 'Pelicans'],
  
  // North Carolina
  'charlotte': ['Hornets', 'Panthers', 'Charlotte FC'],
  'raleigh': ['Hurricanes'],
  
  // Maryland/DC
  'washington': ['Commanders', 'Wizards', 'Nationals', 'Capitals', 'Mystics', 'D.C. United'],
  'dc': ['Commanders', 'Wizards', 'Nationals', 'Capitals', 'Mystics'],
  'washington dc': ['Commanders', 'Wizards', 'Nationals', 'Capitals', 'Mystics'],
  'baltimore': ['Ravens', 'Orioles'],
  
  // Nevada
  'las vegas': ['Raiders', 'Golden Knights', 'Aces'],
  'vegas': ['Raiders', 'Golden Knights', 'Aces'],
  
  // Utah
  'salt lake': ['Jazz', 'Real Salt Lake', 'Utah Hockey Club'],
  'salt lake city': ['Jazz', 'Real Salt Lake'],
  
  // Canada
  'toronto': ['Raptors', 'Blue Jays', 'Maple Leafs', 'Toronto FC'],
  'vancouver': ['Canucks', 'Whitecaps'],
  'montreal': ['Canadiens', 'CF Montreal'],
  'calgary': ['Flames'],
  'edmonton': ['Oilers'],
  'ottawa': ['Senators'],
  'winnipeg': ['Jets'],
};

// Aliases and nicknames (maps alias → canonical team name)
const TEAM_ALIASES = {
  // NBA
  'sixers': '76ers',
  'cavs': 'Cavaliers',
  'blazers': 'Trail Blazers',
  'wolves': 'Timberwolves',
  't-wolves': 'Timberwolves',
  'mavs': 'Mavericks',
  'grizz': 'Grizzlies',
  'clips': 'Clippers',
  'dubs': 'Warriors',
  
  // NFL
  'niners': '49ers',
  'bucs': 'Buccaneers',
  'cards': 'Cardinals',
  'pats': 'Patriots',
  'pack': 'Packers',
  'vikes': 'Vikings',
  'bolts': 'Chargers',
  'fins': 'Dolphins',
  'birds': 'Eagles',
  'boys': 'Cowboys',
  'skins': 'Commanders',
  'washington football': 'Commanders',
  
  // NHL
  'leafs': 'Maple Leafs',
  'habs': 'Canadiens',
  'avs': 'Avalanche',
  'pens': 'Penguins',
  'caps': 'Capitals',
  'sens': 'Senators',
  'wings': 'Red Wings',
  'canes': 'Hurricanes',
  'isles': 'Islanders',
  'preds': 'Predators',
  'cbj': 'Blue Jackets',
  'jackets': 'Blue Jackets',
  'knights': 'Golden Knights',
  'vgk': 'Golden Knights',
  
  // MLB
  'jays': 'Blue Jays',
  'sox': 'Red Sox', // Ambiguous - context needed
  'nats': 'Nationals',
  'halos': 'Angels',
  'd-backs': 'Diamondbacks',
  'dbacks': 'Diamondbacks',
  'snakes': 'Diamondbacks',
  "a's": 'Athletics',
  'as': 'Athletics',
  'cubbies': 'Cubs',
  'buccos': 'Pirates',
  'fish': 'Marlins',
  'brew crew': 'Brewers',
  
  // College
  'unc': 'North Carolina',
  'tar heels': 'North Carolina',
  'blue devils': 'Duke',
  'wildcats': 'Kentucky', // Ambiguous
  'jayhawks': 'Kansas',
  'bulldogs': 'Gonzaga', // Ambiguous
  'spartans': 'Michigan State',
  'wolverines': 'Michigan',
  'buckeyes': 'Ohio State',
  'boilermakers': 'Purdue',
  'hoosiers': 'Indiana',
  'fighting illini': 'Illinois',
  'illini': 'Illinois',
  'badgers': 'Wisconsin',
  'hawkeyes': 'Iowa',
  'terps': 'Maryland',
  'terrapins': 'Maryland',
  'longhorns': 'Texas',
  'aggies': 'Texas A&M',
  'sooners': 'Oklahoma',
  'crimson tide': 'Alabama',
  'tide': 'Alabama',
  'vols': 'Tennessee',
  'volunteers': 'Tennessee',
  'gators': 'Florida',
  'razorbacks': 'Arkansas',
  'hogs': 'Arkansas',
  'tigers': 'LSU', // Ambiguous - also Auburn, Clemson, etc.
  'seminoles': 'Florida State',
  'noles': 'Florida State',
  'hurricanes': 'Miami', // Also NHL Carolina
  'canes': 'Miami', // Also NHL Carolina
  'huskies': 'UConn', // Also Washington
  'bruins': 'UCLA', // Also NHL Boston
  'trojans': 'USC',
  'ducks': 'Oregon', // Also NHL Anaheim
  'beavers': 'Oregon State',
  'cougars': 'Washington State', // Also Houston, BYU
  'utes': 'Utah',
  'buffs': 'Colorado',
  'buffaloes': 'Colorado',
  'golden bears': 'California',
  'bears': 'California', // Also NFL Chicago
  'sun devils': 'Arizona State',
  'zags': 'Gonzaga',
};

// Teams that exist in multiple leagues (need context to disambiguate)
const AMBIGUOUS_TEAMS = {
  'Panthers': ['nfl', 'nhl'], // Carolina Panthers (NFL) vs Florida Panthers (NHL)
  'Cardinals': ['nfl', 'mlb'], // Arizona Cardinals (NFL) vs St. Louis Cardinals (MLB)
  'Giants': ['nfl', 'mlb'], // NY Giants (NFL) vs SF Giants (MLB)
  'Rangers': ['nhl', 'mlb'], // NY Rangers (NHL) vs Texas Rangers (MLB)
  'Kings': ['nba', 'nhl'], // Sacramento Kings (NBA) vs LA Kings (NHL)
  'Jets': ['nfl', 'nhl'], // NY Jets (NFL) vs Winnipeg Jets (NHL)
  'Bruins': ['nhl', 'ncaab'], // Boston Bruins (NHL) vs UCLA Bruins (NCAAB)
  'Ducks': ['nhl', 'ncaab', 'ncaaf'], // Anaheim Ducks (NHL) vs Oregon Ducks
  'Hurricanes': ['nhl', 'ncaab', 'ncaaf'], // Carolina Hurricanes (NHL) vs Miami Hurricanes
  'Stars': ['nhl', 'wnba'], // Dallas Stars (NHL) vs... context needed
  'Lightning': ['nhl'], // Tampa Bay - usually unambiguous
  'Wild': ['nhl', 'wnba'], // Minnesota Wild (NHL)
  'Flames': ['nhl', 'ncaaf'], // Calgary Flames (NHL) vs Liberty Flames
  'Tigers': ['mlb', 'ncaab', 'ncaaf'], // Detroit Tigers, LSU, Clemson, Auburn, etc.
  'Bears': ['nfl', 'ncaab'], // Chicago Bears vs Cal Bears
  'Cougars': ['ncaab', 'ncaaf'], // Houston, BYU, Washington State
  'Wildcats': ['ncaab', 'ncaaf'], // Kentucky, Arizona, K-State, Northwestern
  'Bulldogs': ['ncaab', 'ncaaf'], // Gonzaga, Georgia, Miss State, etc.
};

// Market-to-sport mapping (helps disambiguate)
const MARKET_SPORT_HINTS = {
  // Hockey-specific
  'goals': 'hockey',
  'saves': 'hockey',
  'shots on goal': 'hockey',
  'sog': 'hockey',
  'power play': 'hockey',
  'penalty minutes': 'hockey',
  'pim': 'hockey',
  'faceoffs': 'hockey',
  'period': 'hockey', // Can also be basketball quarters
  
  // Baseball-specific
  'hits': 'baseball',
  'home runs': 'baseball',
  'hr': 'baseball',
  'rbis': 'baseball',
  'rbi': 'baseball',
  'runs': 'baseball',
  'strikeouts': 'baseball',
  'innings': 'baseball',
  'era': 'baseball',
  'batting average': 'baseball',
  'stolen bases': 'baseball',
  'walks': 'baseball',
  
  // Football-specific
  'passing yards': 'football',
  'rushing yards': 'football',
  'receiving yards': 'football',
  'touchdowns': 'football',
  'tds': 'football',
  'interceptions': 'football',
  'sacks': 'football',
  'completions': 'football',
  'attempts': 'football',
  'receptions': 'football',
  'targets': 'football',
  'yards after catch': 'football',
  'yac': 'football',
  'first downs': 'football',
  'field goals': 'football', // Also basketball
  'extra points': 'football',
  
  // Basketball-specific
  'points': 'basketball',
  'assists': 'basketball',
  'rebounds': 'basketball',
  'blocks': 'basketball',
  'steals': 'basketball',
  'turnovers': 'basketball',
  'threes': 'basketball',
  '3-pointers': 'basketball',
  'free throws': 'basketball',
  'double-double': 'basketball',
  'triple-double': 'basketball',
  'minutes': 'basketball',
  
  // Soccer-specific
  'clean sheet': 'soccer',
  'yellow cards': 'soccer',
  'red cards': 'soccer',
  'corners': 'soccer',
  'offsides': 'soccer',
  'possession': 'soccer',
};

/**
 * 🔤 Simple Levenshtein distance for fuzzy matching
 * Returns the edit distance between two strings
 */
function levenshteinDistance(str1, str2) {
  const m = str1.length;
  const n = str2.length;
  
  // Quick checks
  if (m === 0) return n;
  if (n === 0) return m;
  if (str1 === str2) return 0;
  
  // Create distance matrix
  const dp = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));
  
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,      // deletion
        dp[i][j - 1] + 1,      // insertion
        dp[i - 1][j - 1] + cost // substitution
      );
    }
  }
  
  return dp[m][n];
}

/**
 * 🔍 Fuzzy match team name with typo tolerance
 * Returns { match: string, confidence: number, distance: number } or null
 */
function fuzzyMatchTeam(input, maxDistance = 2) {
  const inputLower = input.toLowerCase().trim();
  
  // First check exact match (O(1) with Map)
  if (TEAM_LOOKUP_MAP.has(inputLower)) {
    return { match: TEAM_LOOKUP_MAP.get(inputLower).canonical, confidence: 1.0, distance: 0 };
  }
  
  // Check aliases
  const aliasLower = inputLower;
  if (TEAM_ALIASES[aliasLower]) {
    return { match: TEAM_ALIASES[aliasLower], confidence: 0.95, distance: 0 };
  }
  
  // Fuzzy match - only if input is at least 4 chars (avoid false positives)
  if (inputLower.length < 4) return null;
  
  let bestMatch = null;
  let bestDistance = maxDistance + 1;
  
  for (const [teamLower, info] of TEAM_LOOKUP_MAP.entries()) {
    // Skip very different length strings
    if (Math.abs(teamLower.length - inputLower.length) > maxDistance) continue;
    
    const distance = levenshteinDistance(inputLower, teamLower);
    if (distance <= maxDistance && distance < bestDistance) {
      bestDistance = distance;
      bestMatch = info.canonical;
    }
  }
  
  if (bestMatch) {
    // Confidence decreases with distance
    const confidence = 1 - (bestDistance / (inputLower.length + 1));
    return { match: bestMatch, confidence, distance: bestDistance };
  }
  
  return null;
}

/**
 * 🎯 Disambiguate team based on context
 * Uses market type, query keywords, and seasonality
 */
function disambiguateTeam(teamName, context = {}) {
  const { market, query = '', preferredSport = null } = context;
  const queryLower = query.toLowerCase();
  
  // Check if this team is ambiguous
  const ambiguousLeagues = AMBIGUOUS_TEAMS[teamName];
  if (!ambiguousLeagues || ambiguousLeagues.length === 1) {
    // Not ambiguous, return from main mapping
    return TEAM_TO_LEAGUE[teamName] || null;
  }
  
  // Try to disambiguate using market hints
  if (market) {
    const marketLower = market.toLowerCase();
    for (const [marketKeyword, sport] of Object.entries(MARKET_SPORT_HINTS)) {
      if (marketLower.includes(marketKeyword)) {
        // Find which league matches this sport
        for (const league of ambiguousLeagues) {
          const leagueInfo = getLeagueSport(league);
          if (leagueInfo && leagueInfo.sport === sport) {
            console.log(`[Disambiguate] "${teamName}" → ${league} (market hint: ${marketKeyword})`);
            return { league, sport };
          }
        }
      }
    }
  }
  
  // Try to disambiguate using explicit league keywords in query
  for (const league of ambiguousLeagues) {
    if (queryLower.includes(league)) {
      const leagueInfo = getLeagueSport(league);
      console.log(`[Disambiguate] "${teamName}" → ${league} (explicit keyword)`);
      return leagueInfo;
    }
  }
  
  // Try to disambiguate using sport keywords
  const sportKeywords = {
    'hockey': 'nhl',
    'baseball': 'mlb',
    'football': 'nfl',
    'basketball': 'nba',
    'soccer': 'mls',
    'college basketball': 'ncaab',
    'college football': 'ncaaf',
  };
  
  for (const [keyword, league] of Object.entries(sportKeywords)) {
    if (queryLower.includes(keyword) && ambiguousLeagues.includes(league)) {
      const leagueInfo = getLeagueSport(league);
      console.log(`[Disambiguate] "${teamName}" → ${league} (sport keyword: ${keyword})`);
      return leagueInfo;
    }
  }
  
  // Use preferred sport if provided
  if (preferredSport) {
    for (const league of ambiguousLeagues) {
      const leagueInfo = getLeagueSport(league);
      if (leagueInfo && leagueInfo.sport === preferredSport) {
        console.log(`[Disambiguate] "${teamName}" → ${league} (preferred sport)`);
        return leagueInfo;
      }
    }
  }
  
  // Seasonality-based disambiguation (rough heuristic)
  const month = new Date().getMonth(); // 0-11
  const inSeason = {
    nhl: month >= 9 || month <= 5, // Oct-June
    mlb: month >= 2 && month <= 9, // March-Oct
    nfl: month >= 8 || month <= 1, // Sept-Feb
    nba: month >= 9 || month <= 5, // Oct-June
    ncaab: month >= 10 || month <= 3, // Nov-April
    ncaaf: month >= 7 && month <= 0, // Aug-Jan
  };
  
  // Prefer leagues that are in season
  for (const league of ambiguousLeagues) {
    if (inSeason[league]) {
      const leagueInfo = getLeagueSport(league);
      console.log(`[Disambiguate] "${teamName}" → ${league} (in season)`);
      return leagueInfo;
    }
  }
  
  // Default to first option
  const defaultLeague = ambiguousLeagues[0];
  console.log(`[Disambiguate] "${teamName}" → ${defaultLeague} (default)`);
  return getLeagueSport(defaultLeague);
}

/**
 * Helper to get sport from league
 */
function getLeagueSport(league) {
  const leagueSports = {
    nba: { league: 'nba', sport: 'basketball' },
    wnba: { league: 'wnba', sport: 'basketball' },
    nfl: { league: 'nfl', sport: 'football' },
    nhl: { league: 'nhl', sport: 'hockey' },
    mlb: { league: 'mlb', sport: 'baseball' },
    mls: { league: 'mls', sport: 'soccer' },
    ncaab: { league: 'ncaab', sport: 'basketball' },
    ncaaf: { league: 'ncaaf', sport: 'football' },
  };
  return leagueSports[league] || null;
}

/**
 * 🏙️ Find teams by city name
 * Returns array of possible teams with their leagues
 */
function findTeamsByCity(cityName) {
  const cityLower = cityName.toLowerCase().trim();
  const teams = CITY_TO_TEAMS[cityLower];
  
  if (!teams) return [];
  
  return teams.map(teamName => ({
    team: teamName,
    ...TEAM_TO_LEAGUE[teamName]
  })).filter(t => t.league); // Only return teams we have mappings for
}

/**
 * 🏀🏈🏒⚾ Comprehensive Team-to-League Mapping
 * Maps team names to their respective leagues and sports
 */
const TEAM_TO_LEAGUE = {
  // === NBA (30 teams) ===
  'Lakers': { league: 'nba', sport: 'basketball' },
  'Celtics': { league: 'nba', sport: 'basketball' },
  'Warriors': { league: 'nba', sport: 'basketball' },
  'Heat': { league: 'nba', sport: 'basketball' },
  'Nets': { league: 'nba', sport: 'basketball' },
  'Bucks': { league: 'nba', sport: 'basketball' },
  'Suns': { league: 'nba', sport: 'basketball' },
  'Clippers': { league: 'nba', sport: 'basketball' },
  'Nuggets': { league: 'nba', sport: 'basketball' },
  'Mavericks': { league: 'nba', sport: 'basketball' },
  'Grizzlies': { league: 'nba', sport: 'basketball' },
  'Pelicans': { league: 'nba', sport: 'basketball' },
  'Kings': { league: 'nba', sport: 'basketball' },
  'Timberwolves': { league: 'nba', sport: 'basketball' },
  'Thunder': { league: 'nba', sport: 'basketball' },
  'Trail Blazers': { league: 'nba', sport: 'basketball' },
  'Blazers': { league: 'nba', sport: 'basketball' },
  'Jazz': { league: 'nba', sport: 'basketball' },
  'Rockets': { league: 'nba', sport: 'basketball' },
  'Spurs': { league: 'nba', sport: 'basketball' },
  'Hawks': { league: 'nba', sport: 'basketball' },
  'Hornets': { league: 'nba', sport: 'basketball' },
  'Bulls': { league: 'nba', sport: 'basketball' },
  'Cavaliers': { league: 'nba', sport: 'basketball' },
  'Cavs': { league: 'nba', sport: 'basketball' },
  'Pistons': { league: 'nba', sport: 'basketball' },
  'Pacers': { league: 'nba', sport: 'basketball' },
  'Magic': { league: 'nba', sport: 'basketball' },
  '76ers': { league: 'nba', sport: 'basketball' },
  'Sixers': { league: 'nba', sport: 'basketball' },
  'Knicks': { league: 'nba', sport: 'basketball' },
  'Raptors': { league: 'nba', sport: 'basketball' },
  'Wizards': { league: 'nba', sport: 'basketball' },

  // === WNBA (12 teams) ===
  'Aces': { league: 'wnba', sport: 'basketball' },
  'Lynx': { league: 'wnba', sport: 'basketball' },
  'Liberty': { league: 'wnba', sport: 'basketball' },
  'Sun': { league: 'wnba', sport: 'basketball' },
  'Storm': { league: 'wnba', sport: 'basketball' },
  'Sky': { league: 'wnba', sport: 'basketball' },
  'Fever': { league: 'wnba', sport: 'basketball' },
  'Wings': { league: 'wnba', sport: 'basketball' },
  'Sparks': { league: 'wnba', sport: 'basketball' },
  'Mercury': { league: 'wnba', sport: 'basketball' },
  'Mystics': { league: 'wnba', sport: 'basketball' },
  'Dream': { league: 'wnba', sport: 'basketball' },

  // === NFL (32 teams) ===
  'Bills': { league: 'nfl', sport: 'football' },
  'Dolphins': { league: 'nfl', sport: 'football' },
  'Patriots': { league: 'nfl', sport: 'football' },
  'Jets': { league: 'nfl', sport: 'football' },
  'Ravens': { league: 'nfl', sport: 'football' },
  'Bengals': { league: 'nfl', sport: 'football' },
  'Browns': { league: 'nfl', sport: 'football' },
  'Steelers': { league: 'nfl', sport: 'football' },
  'Texans': { league: 'nfl', sport: 'football' },
  'Colts': { league: 'nfl', sport: 'football' },
  'Jaguars': { league: 'nfl', sport: 'football' },
  'Titans': { league: 'nfl', sport: 'football' },
  'Broncos': { league: 'nfl', sport: 'football' },
  'Chiefs': { league: 'nfl', sport: 'football' },
  'Raiders': { league: 'nfl', sport: 'football' },
  'Chargers': { league: 'nfl', sport: 'football' },
  'Cowboys': { league: 'nfl', sport: 'football' },
  'Giants': { league: 'nfl', sport: 'football' },
  'Eagles': { league: 'nfl', sport: 'football' },
  'Commanders': { league: 'nfl', sport: 'football' },
  'Washington': { league: 'nfl', sport: 'football' },
  'Bears': { league: 'nfl', sport: 'football' },
  'Lions': { league: 'nfl', sport: 'football' },
  'Packers': { league: 'nfl', sport: 'football' },
  'Vikings': { league: 'nfl', sport: 'football' },
  'Falcons': { league: 'nfl', sport: 'football' },
  'Panthers': { league: 'nfl', sport: 'football' },
  'Saints': { league: 'nfl', sport: 'football' },
  'Buccaneers': { league: 'nfl', sport: 'football' },
  'Bucs': { league: 'nfl', sport: 'football' },
  '49ers': { league: 'nfl', sport: 'football' },
  'Niners': { league: 'nfl', sport: 'football' },
  'Cardinals': { league: 'nfl', sport: 'football' },
  'Rams': { league: 'nfl', sport: 'football' },
  'Seahawks': { league: 'nfl', sport: 'football' },

  // === NHL (32 teams) ===
  'Bruins': { league: 'nhl', sport: 'hockey' },
  'Sabres': { league: 'nhl', sport: 'hockey' },
  'Red Wings': { league: 'nhl', sport: 'hockey' },
  'Panthers': { league: 'nhl', sport: 'hockey' }, // Florida Panthers - also NFL, will prioritize by context
  'Canadiens': { league: 'nhl', sport: 'hockey' },
  'Habs': { league: 'nhl', sport: 'hockey' },
  'Senators': { league: 'nhl', sport: 'hockey' },
  'Lightning': { league: 'nhl', sport: 'hockey' },
  'Maple Leafs': { league: 'nhl', sport: 'hockey' },
  'Leafs': { league: 'nhl', sport: 'hockey' },
  'Hurricanes': { league: 'nhl', sport: 'hockey' },
  'Blue Jackets': { league: 'nhl', sport: 'hockey' },
  'Devils': { league: 'nhl', sport: 'hockey' },
  'Islanders': { league: 'nhl', sport: 'hockey' },
  'Rangers': { league: 'nhl', sport: 'hockey' },
  'Flyers': { league: 'nhl', sport: 'hockey' },
  'Penguins': { league: 'nhl', sport: 'hockey' },
  'Capitals': { league: 'nhl', sport: 'hockey' },
  'Caps': { league: 'nhl', sport: 'hockey' },
  'Blackhawks': { league: 'nhl', sport: 'hockey' },
  'Avalanche': { league: 'nhl', sport: 'hockey' },
  'Avs': { league: 'nhl', sport: 'hockey' },
  'Stars': { league: 'nhl', sport: 'hockey' },
  'Wild': { league: 'nhl', sport: 'hockey' },
  'Predators': { league: 'nhl', sport: 'hockey' },
  'Preds': { league: 'nhl', sport: 'hockey' },
  'Blues': { league: 'nhl', sport: 'hockey' },
  'Jets': { league: 'nhl', sport: 'hockey' }, // Winnipeg Jets - also NFL
  'Coyotes': { league: 'nhl', sport: 'hockey' },
  'Utah Hockey Club': { league: 'nhl', sport: 'hockey' },
  'Ducks': { league: 'nhl', sport: 'hockey' },
  'Flames': { league: 'nhl', sport: 'hockey' },
  'Oilers': { league: 'nhl', sport: 'hockey' },
  'Kings': { league: 'nhl', sport: 'hockey' }, // LA Kings - also NBA Sacramento Kings
  'Sharks': { league: 'nhl', sport: 'hockey' },
  'Kraken': { league: 'nhl', sport: 'hockey' },
  'Canucks': { league: 'nhl', sport: 'hockey' },
  'Golden Knights': { league: 'nhl', sport: 'hockey' },
  'Knights': { league: 'nhl', sport: 'hockey' },

  // === MLB (30 teams) ===
  'Orioles': { league: 'mlb', sport: 'baseball' },
  'Red Sox': { league: 'mlb', sport: 'baseball' },
  'Yankees': { league: 'mlb', sport: 'baseball' },
  'Rays': { league: 'mlb', sport: 'baseball' },
  'Blue Jays': { league: 'mlb', sport: 'baseball' },
  'Jays': { league: 'mlb', sport: 'baseball' },
  'Guardians': { league: 'mlb', sport: 'baseball' },
  'Twins': { league: 'mlb', sport: 'baseball' },
  'White Sox': { league: 'mlb', sport: 'baseball' },
  'Royals': { league: 'mlb', sport: 'baseball' },
  'Tigers': { league: 'mlb', sport: 'baseball' },
  'Astros': { league: 'mlb', sport: 'baseball' },
  'Angels': { league: 'mlb', sport: 'baseball' },
  'Athletics': { league: 'mlb', sport: 'baseball' },
  "A's": { league: 'mlb', sport: 'baseball' },
  'Mariners': { league: 'mlb', sport: 'baseball' },
  'Rangers': { league: 'mlb', sport: 'baseball' }, // Texas Rangers - also NHL
  'Braves': { league: 'mlb', sport: 'baseball' },
  'Marlins': { league: 'mlb', sport: 'baseball' },
  'Mets': { league: 'mlb', sport: 'baseball' },
  'Phillies': { league: 'mlb', sport: 'baseball' },
  'Nationals': { league: 'mlb', sport: 'baseball' },
  'Nats': { league: 'mlb', sport: 'baseball' },
  'Cubs': { league: 'mlb', sport: 'baseball' },
  'Reds': { league: 'mlb', sport: 'baseball' },
  'Brewers': { league: 'mlb', sport: 'baseball' },
  'Pirates': { league: 'mlb', sport: 'baseball' },
  'Cardinals': { league: 'mlb', sport: 'baseball' }, // STL Cardinals - also NFL Arizona
  'Diamondbacks': { league: 'mlb', sport: 'baseball' },
  'D-backs': { league: 'mlb', sport: 'baseball' },
  'Rockies': { league: 'mlb', sport: 'baseball' },
  'Dodgers': { league: 'mlb', sport: 'baseball' },
  'Padres': { league: 'mlb', sport: 'baseball' },
  'Giants': { league: 'mlb', sport: 'baseball' }, // SF Giants - also NFL

  // === MLS (29 teams) ===
  'Atlanta United': { league: 'mls', sport: 'soccer' },
  'Austin FC': { league: 'mls', sport: 'soccer' },
  'Charlotte FC': { league: 'mls', sport: 'soccer' },
  'Chicago Fire': { league: 'mls', sport: 'soccer' },
  'FC Cincinnati': { league: 'mls', sport: 'soccer' },
  'Colorado Rapids': { league: 'mls', sport: 'soccer' },
  'Rapids': { league: 'mls', sport: 'soccer' },
  'Columbus Crew': { league: 'mls', sport: 'soccer' },
  'Crew': { league: 'mls', sport: 'soccer' },
  'FC Dallas': { league: 'mls', sport: 'soccer' },
  'D.C. United': { league: 'mls', sport: 'soccer' },
  'Houston Dynamo': { league: 'mls', sport: 'soccer' },
  'Dynamo': { league: 'mls', sport: 'soccer' },
  'Sporting Kansas City': { league: 'mls', sport: 'soccer' },
  'Sporting KC': { league: 'mls', sport: 'soccer' },
  'LA Galaxy': { league: 'mls', sport: 'soccer' },
  'Galaxy': { league: 'mls', sport: 'soccer' },
  'LAFC': { league: 'mls', sport: 'soccer' },
  'Inter Miami': { league: 'mls', sport: 'soccer' },
  'CF Montreal': { league: 'mls', sport: 'soccer' },
  'Nashville SC': { league: 'mls', sport: 'soccer' },
  'New England Revolution': { league: 'mls', sport: 'soccer' },
  'Revolution': { league: 'mls', sport: 'soccer' },
  'NYCFC': { league: 'mls', sport: 'soccer' },
  'New York City FC': { league: 'mls', sport: 'soccer' },
  'New York Red Bulls': { league: 'mls', sport: 'soccer' },
  'Red Bulls': { league: 'mls', sport: 'soccer' },
  'Orlando City': { league: 'mls', sport: 'soccer' },
  'Philadelphia Union': { league: 'mls', sport: 'soccer' },
  'Union': { league: 'mls', sport: 'soccer' },
  'Portland Timbers': { league: 'mls', sport: 'soccer' },
  'Timbers': { league: 'mls', sport: 'soccer' },
  'Real Salt Lake': { league: 'mls', sport: 'soccer' },
  'RSL': { league: 'mls', sport: 'soccer' },
  'San Jose Earthquakes': { league: 'mls', sport: 'soccer' },
  'Earthquakes': { league: 'mls', sport: 'soccer' },
  'Seattle Sounders': { league: 'mls', sport: 'soccer' },
  'Sounders': { league: 'mls', sport: 'soccer' },
  'St. Louis City SC': { league: 'mls', sport: 'soccer' },
  'Toronto FC': { league: 'mls', sport: 'soccer' },
  'Vancouver Whitecaps': { league: 'mls', sport: 'soccer' },
  'Whitecaps': { league: 'mls', sport: 'soccer' },

  // === NCAAB (College Basketball - Top Programs) ===
  'Duke': { league: 'ncaab', sport: 'basketball' },
  'Blue Devils': { league: 'ncaab', sport: 'basketball' },
  'North Carolina': { league: 'ncaab', sport: 'basketball' },
  'Tar Heels': { league: 'ncaab', sport: 'basketball' },
  'UNC': { league: 'ncaab', sport: 'basketball' },
  'Kentucky': { league: 'ncaab', sport: 'basketball' },
  'Wildcats': { league: 'ncaab', sport: 'basketball' }, // Multiple schools
  'Kansas': { league: 'ncaab', sport: 'basketball' },
  'Jayhawks': { league: 'ncaab', sport: 'basketball' },
  'Gonzaga': { league: 'ncaab', sport: 'basketball' },
  'Bulldogs': { league: 'ncaab', sport: 'basketball' },
  'Villanova': { league: 'ncaab', sport: 'basketball' },
  'Michigan': { league: 'ncaab', sport: 'basketball' },
  'Wolverines': { league: 'ncaab', sport: 'basketball' },
  'Michigan State': { league: 'ncaab', sport: 'basketball' },
  'Spartans': { league: 'ncaab', sport: 'basketball' },
  'MSU': { league: 'ncaab', sport: 'basketball' },
  'Ohio State': { league: 'ncaab', sport: 'basketball' },
  'Buckeyes': { league: 'ncaab', sport: 'basketball' },
  'Purdue': { league: 'ncaab', sport: 'basketball' },
  'Boilermakers': { league: 'ncaab', sport: 'basketball' },
  'Indiana': { league: 'ncaab', sport: 'basketball' },
  'Hoosiers': { league: 'ncaab', sport: 'basketball' },
  'Illinois': { league: 'ncaab', sport: 'basketball' },
  'Fighting Illini': { league: 'ncaab', sport: 'basketball' },
  'Illini': { league: 'ncaab', sport: 'basketball' },
  'Wisconsin': { league: 'ncaab', sport: 'basketball' },
  'Badgers': { league: 'ncaab', sport: 'basketball' },
  'Iowa': { league: 'ncaab', sport: 'basketball' },
  'Hawkeyes': { league: 'ncaab', sport: 'basketball' },
  'Maryland': { league: 'ncaab', sport: 'basketball' },
  'Terrapins': { league: 'ncaab', sport: 'basketball' },
  'Terps': { league: 'ncaab', sport: 'basketball' },
  'Penn State': { league: 'ncaab', sport: 'basketball' },
  'Nittany Lions': { league: 'ncaab', sport: 'basketball' },
  'Nebraska': { league: 'ncaab', sport: 'basketball' },
  'Cornhuskers': { league: 'ncaab', sport: 'basketball' },
  'Northwestern': { league: 'ncaab', sport: 'basketball' },
  'Rutgers': { league: 'ncaab', sport: 'basketball' },
  'Scarlet Knights': { league: 'ncaab', sport: 'basketball' },
  'Baylor': { league: 'ncaab', sport: 'basketball' },
  'Texas': { league: 'ncaab', sport: 'basketball' },
  'Longhorns': { league: 'ncaab', sport: 'basketball' },
  'Texas Tech': { league: 'ncaab', sport: 'basketball' },
  'Red Raiders': { league: 'ncaab', sport: 'basketball' },
  'Oklahoma': { league: 'ncaab', sport: 'basketball' },
  'Sooners': { league: 'ncaab', sport: 'basketball' },
  'Oklahoma State': { league: 'ncaab', sport: 'basketball' },
  'Cowboys': { league: 'ncaab', sport: 'basketball' }, // Also NFL
  'Kansas State': { league: 'ncaab', sport: 'basketball' },
  'Iowa State': { league: 'ncaab', sport: 'basketball' },
  'Cyclones': { league: 'ncaab', sport: 'basketball' },
  'West Virginia': { league: 'ncaab', sport: 'basketball' },
  'Mountaineers': { league: 'ncaab', sport: 'basketball' },
  'TCU': { league: 'ncaab', sport: 'basketball' },
  'Horned Frogs': { league: 'ncaab', sport: 'basketball' },
  'Tennessee': { league: 'ncaab', sport: 'basketball' },
  'Volunteers': { league: 'ncaab', sport: 'basketball' },
  'Vols': { league: 'ncaab', sport: 'basketball' },
  'Alabama': { league: 'ncaab', sport: 'basketball' },
  'Crimson Tide': { league: 'ncaab', sport: 'basketball' },
  'Auburn': { league: 'ncaab', sport: 'basketball' },
  'Florida': { league: 'ncaab', sport: 'basketball' },
  'Gators': { league: 'ncaab', sport: 'basketball' },
  'Arkansas': { league: 'ncaab', sport: 'basketball' },
  'Razorbacks': { league: 'ncaab', sport: 'basketball' },
  'LSU': { league: 'ncaab', sport: 'basketball' },
  'Ole Miss': { league: 'ncaab', sport: 'basketball' },
  'Rebels': { league: 'ncaab', sport: 'basketball' },
  'Mississippi State': { league: 'ncaab', sport: 'basketball' },
  'Missouri': { league: 'ncaab', sport: 'basketball' },
  'Mizzou': { league: 'ncaab', sport: 'basketball' },
  'Texas A&M': { league: 'ncaab', sport: 'basketball' },
  'Aggies': { league: 'ncaab', sport: 'basketball' },
  'South Carolina': { league: 'ncaab', sport: 'basketball' },
  'Gamecocks': { league: 'ncaab', sport: 'basketball' },
  'Georgia': { league: 'ncaab', sport: 'basketball' },
  'Vanderbilt': { league: 'ncaab', sport: 'basketball' },
  'Commodores': { league: 'ncaab', sport: 'basketball' },
  'UCLA': { league: 'ncaab', sport: 'basketball' },
  'Bruins': { league: 'ncaab', sport: 'basketball' }, // Also NHL Boston
  'USC': { league: 'ncaab', sport: 'basketball' },
  'Trojans': { league: 'ncaab', sport: 'basketball' },
  'Arizona': { league: 'ncaab', sport: 'basketball' },
  'Oregon': { league: 'ncaab', sport: 'basketball' },
  'Ducks': { league: 'ncaab', sport: 'basketball' }, // Also NHL Anaheim
  'Stanford': { league: 'ncaab', sport: 'basketball' },
  'Cardinal': { league: 'ncaab', sport: 'basketball' },
  'Colorado': { league: 'ncaab', sport: 'basketball' },
  'Buffaloes': { league: 'ncaab', sport: 'basketball' },
  'Buffs': { league: 'ncaab', sport: 'basketball' },
  'Utah': { league: 'ncaab', sport: 'basketball' },
  'Utes': { league: 'ncaab', sport: 'basketball' },
  'Arizona State': { league: 'ncaab', sport: 'basketball' },
  'Sun Devils': { league: 'ncaab', sport: 'basketball' },
  'ASU': { league: 'ncaab', sport: 'basketball' },
  'California': { league: 'ncaab', sport: 'basketball' },
  'Golden Bears': { league: 'ncaab', sport: 'basketball' },
  'Cal': { league: 'ncaab', sport: 'basketball' },
  'Oregon State': { league: 'ncaab', sport: 'basketball' },
  'Beavers': { league: 'ncaab', sport: 'basketball' },
  'Washington State': { league: 'ncaab', sport: 'basketball' },
  'Cougars': { league: 'ncaab', sport: 'basketball' },
  'Virginia': { league: 'ncaab', sport: 'basketball' },
  'Cavaliers': { league: 'ncaab', sport: 'basketball' }, // Also NBA Cleveland
  'Virginia Tech': { league: 'ncaab', sport: 'basketball' },
  'Hokies': { league: 'ncaab', sport: 'basketball' },
  'Miami': { league: 'ncaab', sport: 'basketball' },
  'Hurricanes': { league: 'ncaab', sport: 'basketball' }, // Also NHL Carolina
  'Syracuse': { league: 'ncaab', sport: 'basketball' },
  'Orange': { league: 'ncaab', sport: 'basketball' },
  'Louisville': { league: 'ncaab', sport: 'basketball' },
  'Clemson': { league: 'ncaab', sport: 'basketball' },
  'Notre Dame': { league: 'ncaab', sport: 'basketball' },
  'Fighting Irish': { league: 'ncaab', sport: 'basketball' },
  'NC State': { league: 'ncaab', sport: 'basketball' },
  'Wolfpack': { league: 'ncaab', sport: 'basketball' },
  'Wake Forest': { league: 'ncaab', sport: 'basketball' },
  'Demon Deacons': { league: 'ncaab', sport: 'basketball' },
  'Deacs': { league: 'ncaab', sport: 'basketball' },
  'Boston College': { league: 'ncaab', sport: 'basketball' },
  'Florida State': { league: 'ncaab', sport: 'basketball' },
  'Seminoles': { league: 'ncaab', sport: 'basketball' },
  'FSU': { league: 'ncaab', sport: 'basketball' },
  'Georgia Tech': { league: 'ncaab', sport: 'basketball' },
  'Yellow Jackets': { league: 'ncaab', sport: 'basketball' },
  'Pittsburgh': { league: 'ncaab', sport: 'basketball' },
  'Pitt': { league: 'ncaab', sport: 'basketball' },
  'UConn': { league: 'ncaab', sport: 'basketball' },
  'Connecticut': { league: 'ncaab', sport: 'basketball' },
  'Huskies': { league: 'ncaab', sport: 'basketball' },
  'Creighton': { league: 'ncaab', sport: 'basketball' },
  'Bluejays': { league: 'ncaab', sport: 'basketball' },
  'Xavier': { league: 'ncaab', sport: 'basketball' },
  'Musketeers': { league: 'ncaab', sport: 'basketball' },
  'Butler': { league: 'ncaab', sport: 'basketball' },
  'Marquette': { league: 'ncaab', sport: 'basketball' },
  'Golden Eagles': { league: 'ncaab', sport: 'basketball' },
  'Providence': { league: 'ncaab', sport: 'basketball' },
  'Friars': { league: 'ncaab', sport: 'basketball' },
  "St. John's": { league: 'ncaab', sport: 'basketball' },
  'Seton Hall': { league: 'ncaab', sport: 'basketball' },
  'Georgetown': { league: 'ncaab', sport: 'basketball' },
  'Hoyas': { league: 'ncaab', sport: 'basketball' },
  'DePaul': { league: 'ncaab', sport: 'basketball' },
  'Blue Demons': { league: 'ncaab', sport: 'basketball' },
  'Memphis': { league: 'ncaab', sport: 'basketball' },
  'Houston': { league: 'ncaab', sport: 'basketball' },
  'Cincinnati': { league: 'ncaab', sport: 'basketball' },
  'Bearcats': { league: 'ncaab', sport: 'basketball' },
  'SMU': { league: 'ncaab', sport: 'basketball' },
  'Mustangs': { league: 'ncaab', sport: 'basketball' },
  'UCF': { league: 'ncaab', sport: 'basketball' },
  'BYU': { league: 'ncaab', sport: 'basketball' },
  "Saint Mary's": { league: 'ncaab', sport: 'basketball' },
  'Gaels': { league: 'ncaab', sport: 'basketball' },
  'San Diego State': { league: 'ncaab', sport: 'basketball' },
  'SDSU': { league: 'ncaab', sport: 'basketball' },
  'Aztecs': { league: 'ncaab', sport: 'basketball' },
  'Nevada': { league: 'ncaab', sport: 'basketball' },
  'Wolf Pack': { league: 'ncaab', sport: 'basketball' },
  'Boise State': { league: 'ncaab', sport: 'basketball' },
  'Broncos': { league: 'ncaab', sport: 'basketball' }, // Also NFL Denver
  'UNLV': { league: 'ncaab', sport: 'basketball' },
  'Runnin Rebels': { league: 'ncaab', sport: 'basketball' },
  'VCU': { league: 'ncaab', sport: 'basketball' },
  'Davidson': { league: 'ncaab', sport: 'basketball' },
  'Dayton': { league: 'ncaab', sport: 'basketball' },
  'Flyers': { league: 'ncaab', sport: 'basketball' }, // Also NHL Philadelphia
  'Richmond': { league: 'ncaab', sport: 'basketball' },
  'Spiders': { league: 'ncaab', sport: 'basketball' },
  "St. Bonaventure": { league: 'ncaab', sport: 'basketball' },
  'Bonnies': { league: 'ncaab', sport: 'basketball' },
  
  // === NCAAF (College Football - Top Programs) ===
  // Note: Most college programs share basketball and football teams
  // The context (market type, season) will help disambiguate
  // Adding some football-specific teams
  'Air Force': { league: 'ncaaf', sport: 'football' },
  'Army': { league: 'ncaaf', sport: 'football' },
  'Black Knights': { league: 'ncaaf', sport: 'football' },
  'Navy': { league: 'ncaaf', sport: 'football' },
  'Midshipmen': { league: 'ncaaf', sport: 'football' },
  'Marshall': { league: 'ncaaf', sport: 'football' },
  'Thundering Herd': { league: 'ncaaf', sport: 'football' },
  'Coastal Carolina': { league: 'ncaaf', sport: 'football' },
  'Chanticleers': { league: 'ncaaf', sport: 'football' },
  'Appalachian State': { league: 'ncaaf', sport: 'football' },
  'App State': { league: 'ncaaf', sport: 'football' },
  'Mountaineers': { league: 'ncaaf', sport: 'football' },
  'Liberty': { league: 'ncaaf', sport: 'football' }, // Also WNBA
  'Flames': { league: 'ncaaf', sport: 'football' }, // Also NHL Calgary
  'James Madison': { league: 'ncaaf', sport: 'football' },
  'JMU': { league: 'ncaaf', sport: 'football' },
  'Dukes': { league: 'ncaaf', sport: 'football' },
  'Troy': { league: 'ncaaf', sport: 'football' },
  'Trojans': { league: 'ncaaf', sport: 'football' },
  'Louisiana': { league: 'ncaaf', sport: 'football' },
  'Ragin Cajuns': { league: 'ncaaf', sport: 'football' },
  'ULL': { league: 'ncaaf', sport: 'football' },
};

// 🚀 Initialize the O(1) lookup Map after TEAM_TO_LEAGUE is defined
(function initializeTeamLookupMap() {
  for (const [teamName, info] of Object.entries(TEAM_TO_LEAGUE)) {
    const teamLower = teamName.toLowerCase();
    TEAM_LOOKUP_MAP.set(teamLower, { 
      ...info, 
      canonical: teamName 
    });
  }
  
  // Also add aliases to the lookup map
  for (const [alias, canonical] of Object.entries(TEAM_ALIASES)) {
    const aliasLower = alias.toLowerCase();
    if (!TEAM_LOOKUP_MAP.has(aliasLower) && TEAM_TO_LEAGUE[canonical]) {
      TEAM_LOOKUP_MAP.set(aliasLower, {
        ...TEAM_TO_LEAGUE[canonical],
        canonical,
        isAlias: true
      });
    }
  }
  
  console.log(`[TeamLookup] Initialized ${TEAM_LOOKUP_MAP.size} team entries (O(1) lookups ready)`);
})();

/**
 * 🎯 OPTIMIZED: Get league info from team name with context awareness
 * Features:
 * - O(1) Map lookups
 * - Alias resolution
 * - Fuzzy matching for typos
 * - Context-aware disambiguation for ambiguous teams
 * - City name support
 */
function getLeagueFromTeam(teamName, queryContext = {}) {
  const { market, query = '' } = queryContext;
  const normalizedTeam = teamName.toLowerCase().trim();
  
  // 1️⃣ O(1) exact match via Map
  if (TEAM_LOOKUP_MAP.has(normalizedTeam)) {
    const info = TEAM_LOOKUP_MAP.get(normalizedTeam);
    
    // Check if this team is ambiguous
    if (AMBIGUOUS_TEAMS[info.canonical]) {
      return disambiguateTeam(info.canonical, { market, query });
    }
    
    return { league: info.league, sport: info.sport };
  }
  
  // 2️⃣ Check aliases (also O(1))
  if (TEAM_ALIASES[normalizedTeam]) {
    const canonical = TEAM_ALIASES[normalizedTeam];
    
    // Check if this team is ambiguous
    if (AMBIGUOUS_TEAMS[canonical]) {
      return disambiguateTeam(canonical, { market, query });
    }
    
    return TEAM_TO_LEAGUE[canonical] || null;
  }
  
  // 3️⃣ Check city names
  const cityTeams = findTeamsByCity(normalizedTeam);
  if (cityTeams.length > 0) {
    // If city has only one team for a sport matching the market, use that
    if (market) {
      const sportHint = Object.entries(MARKET_SPORT_HINTS).find(([k]) => 
        market.toLowerCase().includes(k)
      )?.[1];
      
      if (sportHint) {
        const matchingTeam = cityTeams.find(t => t.sport === sportHint);
        if (matchingTeam) {
          console.log(`[TeamLookup] City "${teamName}" + market "${market}" → ${matchingTeam.team}`);
          return { league: matchingTeam.league, sport: matchingTeam.sport };
        }
      }
    }
    
    // Return first team from city as default
    console.log(`[TeamLookup] City "${teamName}" → ${cityTeams[0].team} (first match)`);
    return { league: cityTeams[0].league, sport: cityTeams[0].sport };
  }
  
  // 4️⃣ Fuzzy matching for typos (only for longer inputs)
  if (normalizedTeam.length >= 4) {
    const fuzzyResult = fuzzyMatchTeam(normalizedTeam, 2);
    if (fuzzyResult && fuzzyResult.confidence >= 0.7) {
      console.log(`[TeamLookup] Fuzzy match: "${teamName}" → "${fuzzyResult.match}" (confidence: ${(fuzzyResult.confidence * 100).toFixed(0)}%, distance: ${fuzzyResult.distance})`);
      
      // Check if fuzzy match is ambiguous
      if (AMBIGUOUS_TEAMS[fuzzyResult.match]) {
        return disambiguateTeam(fuzzyResult.match, { market, query });
      }
      
      return TEAM_TO_LEAGUE[fuzzyResult.match] || null;
    }
  }
  
  // 5️⃣ Partial match fallback (for compound names like "golden knights", "blue jackets")
  for (const [key, value] of Object.entries(TEAM_TO_LEAGUE)) {
    const keyLower = key.toLowerCase();
    if (normalizedTeam.includes(keyLower) || keyLower.includes(normalizedTeam)) {
      // Require at least 60% of the shorter string to match
      const minLength = Math.min(normalizedTeam.length, keyLower.length);
      const matchLength = keyLower.length;
      if (matchLength >= minLength * 0.6) {
        console.log(`[TeamLookup] Partial match: "${teamName}" → "${key}"`);
        return value;
      }
    }
  }
  
  // No match found - return null to let AI handle it
  console.log(`[TeamLookup] ❌ No match for "${teamName}" - AI will detect`);
  return null;
}

function parseQueryEntities(prompt) {
  const lower = prompt.toLowerCase();
  
  // Detect query type
  let queryType = 'generic'; // 'player', 'team', 'generic'
  let player = null;
  let team = null;
  let market = null;
  let line = null;
  let direction = null; // 'over' or 'under'
  let league = null;  // 🆕 Add league detection
  let sport = null;   // 🆕 Add sport detection
  
  // Market detection (what stat/bet type) - COMPREHENSIVE
  const marketPatterns = {
    // Player props - specific stats
    points: /\b(points?|pts?|score|scoring)\b/i,
    assists: /\b(assists?|ast|dimes?)\b/i,
    rebounds: /\b(rebounds?|reb|boards?)\b/i,
    threes: /\b(threes?|3s|three pointers?|3-pointers?|threes made)\b/i,
    blocks: /\b(blocks?|blk)\b/i,
    steals: /\b(steals?|stl)\b/i,
    turnovers: /\b(turnovers?|to)\b/i,
    'free-throws': /\b(free throws?|ft|free throws made)\b/i,
    'field-goals': /\b(field goals?|fg|field goals made)\b/i,
    'double-double': /\b(double[- ]double)\b/i,
    'triple-double': /\b(triple[- ]double)\b/i,
    
    // Combo props
    'points+assists': /\b(points?\s*(and|\+|plus)\s*assists?|pts?\s*(and|\+|plus)\s*ast|pa)\b/i,
    'points+rebounds': /\b(points?\s*(and|\+|plus)\s*rebounds?|pts?\s*(and|\+|plus)\s*reb|pr)\b/i,
    'points+assists+rebounds': /\b(points?\s*(and|\+|plus)\s*assists?\s*(and|\+|plus)\s*rebounds?|pts?\s*(and|\+|plus)\s*ast?\s*(and|\+|plus)\s*reb|par)\b/i,
    'assists+rebounds': /\b(assists?\s*(and|\+|plus)\s*rebounds?|ast?\s*(and|\+|plus)\s*reb|ar)\b/i,
    'rebounds+assists': /\b(rebounds?\s*(and|\+|plus)\s*assists?|reb?\s*(and|\+|plus)\s*ast|ra)\b/i,
    'steals+blocks': /\b(steals?\s*(and|\+|plus)\s*blocks?|stl?\s*(and|\+|plus)\s*blk|sb)\b/i,
    
    // Team markets
    moneyline: /\b(moneyline|ml|to win|winner|straight up|su)\b/i,
    spread: /\b(spread|line|handicap|point spread|[+-]\d+\.?\d*)\b/i,
    total: /\b(total|over\/under|o\/u|game total)\b/i,
    
    // NFL/Football specific
    'pass-yards': /\b(pass(ing)?\s*yards?|pass yds?|passing)\b/i,
    'rush-yards': /\b(rush(ing)?\s*yards?|rush yds?|rushing)\b/i,
    'rec-yards': /\b(rec(eiving)?\s*yards?|rec yds?|receiving)\b/i,
    receptions: /\b(receptions?|rec|catches?)\b/i,
    touchdowns: /\b(touchdowns?|td|tds|scores?)\b/i,
    interceptions: /\b(interceptions?|int|ints|picks?)\b/i,
    
    // NHL/Hockey specific
    goals: /\b(goals?)\b/i,
    saves: /\b(saves?)\b/i,
    'shots-on-goal': /\b(shots?\s*on\s*goal|sog)\b/i,
    
    // Baseball specific
    hits: /\b(hits?)\b/i,
    'home-runs': /\b(home\s*runs?|hr|homers?)\b/i,
    rbis: /\b(rbis?|runs?\s*batted\s*in)\b/i,
    strikeouts: /\b(strikeouts?|k|ks|strike\s*outs?)\b/i,
    
    // Generic/Catch-all
    'player-prop': /\b(player\s*prop)\b/i,
    'game-prop': /\b(game\s*prop)\b/i
  };
  
  // Detect market FIRST (so we don't confuse "Lakers Moneyline" as player name)
  for (const [marketName, pattern] of Object.entries(marketPatterns)) {
    if (pattern.test(lower)) {
      market = marketName;
      break;
    }
  }
  
  // 🆕 FALLBACK: If no market recognized, try to extract market keywords from query
  // This handles niche markets like "3rd quarter team total" that aren't in our patterns
  if (!market) {
    // Common market-related keywords that might indicate a market type
    const marketKeywords = ['quarter', 'qtr', 'half', 'period', 'innings?', 'moneyline', 'spread', 'total', 
                           'point', 'assist', 'rebound', 'block', 'steal', 'turnover', 'three', 'combo',
                           'double-double', 'triple-double', 'rush', 'pass', 'rec', 'touchdown', 'td',
                           'hit', 'home run', 'strikeout', 'goal', 'save', 'shot'];
    
    const foundKeywords = [];
    for (const keyword of marketKeywords) {
      const regex = new RegExp(`\\b${keyword}s?\\b`, 'i');
      if (regex.test(prompt)) {
        foundKeywords.push(keyword);
      }
    }
    
    // If we found market-related keywords, join them as the market search term
    if (foundKeywords.length > 0) {
      market = foundKeywords.join(' ');
      console.log(`[Parser] 🔍 No exact market match, using keywords: "${market}"`);
    }
  }
  
  // Line detection (10+, over 25.5, under 3.5, etc.)
  const linePatterns = [
    /(\d+\.?\d*)\s*\+/,  // "10+"
    /over\s+(\d+\.?\d*)/i,  // "over 25.5"
    /under\s+(\d+\.?\d*)/i,  // "under 3.5"
    /[ou]\s*(\d+\.?\d*)/i,  // "o25.5" or "u3.5"
    />\s*(\d+\.?\d*)/,  // ">10"
    /<\s*(\d+\.?\d*)/   // "<3.5"
  ];
  
  for (const pattern of linePatterns) {
    const match = lower.match(pattern);
    if (match) {
      line = parseFloat(match[1]);
      // Detect direction
      if (/over|[ou]\s*\d|>\s*\d|\d+\+/.test(lower)) {
        direction = 'over';
      } else if (/under|<\s*\d/.test(lower)) {
        direction = 'under';
      }
      break;
    }
  }
  
  // Team detection - help identify obvious teams (AI does heavy lifting, cache is source of truth)
  const teamNames = [
    // === NBA (30 teams) ===
    'Lakers', 'Celtics', 'Warriors', 'Heat', 'Nets', 'Bucks', 'Suns', 'Clippers',
    'Nuggets', 'Mavericks', 'Grizzlies', 'Pelicans', 'Kings', 'Timberwolves',
    'Thunder', 'Trail Blazers', 'Blazers', 'Jazz', 'Rockets', 'Spurs',
    'Hawks', 'Hornets', 'Bulls', 'Cavaliers', 'Cavs', 'Pistons', 'Pacers', 'Magic',
    '76ers', 'Sixers', 'Knicks', 'Raptors', 'Wizards',
    
    // === WNBA (12 teams) ===
    'Aces', 'Lynx', 'Liberty', 'Sun', 'Storm', 'Sky', 'Fever', 'Wings',
    'Sparks', 'Mercury', 'Mystics', 'Dream',
    
    // === NFL (32 teams) ===
    // AFC East
    'Bills', 'Dolphins', 'Patriots', 'Jets',
    // AFC North
    'Ravens', 'Bengals', 'Browns', 'Steelers',
    // AFC South
    'Texans', 'Colts', 'Jaguars', 'Titans',
    // AFC West
    'Broncos', 'Chiefs', 'Raiders', 'Chargers',
    // NFC East
    'Cowboys', 'Giants', 'Eagles', 'Commanders', 'Washington',
    // NFC North
    'Bears', 'Lions', 'Packers', 'Vikings',
    // NFC South
    'Falcons', 'Panthers', 'Saints', 'Buccaneers', 'Bucs',
    // NFC West
    '49ers', 'Niners', 'Cardinals', 'Rams', 'Seahawks',
    
    // === NCAAB (Top 100+ programs) ===
    // Power 5 - ACC
    'Duke', 'North Carolina', 'UNC', 'Virginia', 'Virginia Tech', 'Miami', 'Syracuse',
    'Louisville', 'Clemson', 'Notre Dame', 'NC State', 'Wake Forest', 'Boston College',
    'Florida State', 'FSU', 'Georgia Tech', 'Pittsburgh', 'Pitt',
    
    // Power 5 - Big Ten
    'Michigan', 'Michigan State', 'MSU', 'Ohio State', 'Purdue', 'Indiana', 'Illinois',
    'Wisconsin', 'Iowa', 'Maryland', 'Penn State', 'Minnesota', 'Nebraska', 'Northwestern',
    'Rutgers',
    
    // Power 5 - Big 12
    'Kansas', 'Baylor', 'Texas', 'Texas Tech', 'Oklahoma', 'Oklahoma State', 'Kansas State',
    'Iowa State', 'West Virginia', 'TCU',
    
    // Power 5 - SEC
    'Kentucky', 'Tennessee', 'Alabama', 'Auburn', 'Florida', 'Arkansas', 'LSU',
    'Mississippi State', 'Ole Miss', 'Missouri', 'Texas A&M', 'South Carolina', 'Georgia',
    'Vanderbilt',
    
    // Power 5 - Pac-12
    'UCLA', 'USC', 'Arizona', 'Oregon', 'Washington', 'Stanford', 'Colorado',
    'Utah', 'Arizona State', 'ASU', 'California', 'Cal', 'Oregon State', 'Washington State',
    
    // Other Major Programs
    'Gonzaga', 'Villanova', 'Creighton', 'Xavier', 'Butler', 'Marquette', 'Providence',
    'St. John\'s', 'Seton Hall', 'Georgetown', 'DePaul', 'UConn', 'Connecticut',
    'Memphis', 'Houston', 'Cincinnati', 'Wichita State', 'Temple', 'SMU', 'UCF',
    'BYU', 'Saint Mary\'s', 'San Diego State', 'SDSU', 'Nevada', 'Boise State', 'UNLV',
    'VCU', 'Rhode Island', 'Davidson', 'Dayton', 'Richmond', 'St. Bonaventure',
    
    // HBCUs
    'Morgan State', 'Howard', 'Hampton', 'Norfolk State', 'Delaware State',
    'North Carolina A&T', 'NC A&T', 'South Carolina State', 'Florida A&M', 'FAMU',
    'Bethune-Cookman', 'Coppin State', 'Maryland Eastern Shore', 'UMES',
    
    // === NCAAF (FBS - Top conferences) ===
    // Same teams as basketball but for football context
    // Power 5 football programs already covered above
    // Adding FBS-only notable programs
    'Air Force', 'Army', 'Navy', 'Marshall', 'Coastal Carolina', 'Appalachian State',
    'Liberty', 'James Madison', 'JMU', 'Troy', 'Louisiana', 'ULL',
    
    // === NHL (32 teams) ===
    // Atlantic
    'Bruins', 'Sabres', 'Red Wings', 'Panthers', 'Canadiens', 'Senators', 'Lightning', 'Maple Leafs',
    // Metropolitan
    'Hurricanes', 'Blue Jackets', 'Devils', 'Islanders', 'Rangers', 'Flyers', 'Penguins', 'Capitals',
    // Central
    'Blackhawks', 'Avalanche', 'Stars', 'Wild', 'Predators', 'Blues', 'Jets', 'Coyotes',
    // Pacific
    'Ducks', 'Flames', 'Oilers', 'Kings', 'Sharks', 'Kraken', 'Canucks', 'Golden Knights', 'Knights',
    
    // === MLB (30 teams) ===
    // AL East
    'Orioles', 'Red Sox', 'Yankees', 'Rays', 'Blue Jays',
    // AL Central
    'Guardians', 'Twins', 'White Sox', 'Royals', 'Tigers',
    // AL West
    'Astros', 'Angels', 'Athletics', 'A\'s', 'Mariners', 'Rangers',
    // NL East
    'Braves', 'Marlins', 'Mets', 'Phillies', 'Nationals',
    // NL Central
    'Cubs', 'Reds', 'Brewers', 'Pirates', 'Cardinals',
    // NL West
    'Diamondbacks', 'D-backs', 'Rockies', 'Dodgers', 'Padres', 'Giants'
  ];
  
  for (const teamName of teamNames) {
    if (new RegExp(`\\b${teamName}\\b`, 'i').test(prompt)) {
      team = teamName;
      queryType = 'team';
      
      // 🆕 Get league/sport from team name mapping
      const leagueInfo = getLeagueFromTeam(teamName, { market, prompt: lower });
      if (leagueInfo) {
        league = leagueInfo.league;
        sport = leagueInfo.sport;
        console.log(`[Parser] 🏟️ Team "${teamName}" → ${league.toUpperCase()} (${sport})`);
      }
      break;
    }
  }
  
  // 🆕 If no team found yet, check ALIASES (e.g., "avs" → "Avalanche", "pens" → "Penguins")
  if (!team) {
    for (const [alias, canonicalTeam] of Object.entries(TEAM_ALIASES)) {
      if (new RegExp(`\\b${alias}\\b`, 'i').test(lower)) {
        team = canonicalTeam;
        queryType = 'team';
        
        // Get league/sport from the canonical team
        const leagueInfo = getLeagueFromTeam(canonicalTeam, { market, prompt: lower });
        if (leagueInfo) {
          league = leagueInfo.league;
          sport = leagueInfo.sport;
          console.log(`[Parser] 🏟️ Alias "${alias}" → "${canonicalTeam}" → ${league.toUpperCase()} (${sport})`);
        }
        break;
      }
    }
  }
  
  // 🆕 If still no team, try city names
  if (!team) {
    for (const [city, cityTeams] of Object.entries(CITY_TO_TEAMS)) {
      if (new RegExp(`\\b${city}\\b`, 'i').test(lower)) {
        // Use the first team from the city (or better: use market hints)
        const firstTeam = cityTeams[0];
        if (firstTeam && TEAM_TO_LEAGUE[firstTeam]) {
          team = firstTeam;
          queryType = 'team';
          
          // Try to pick the right team based on market hints
          const leagueInfo = getLeagueFromTeam(city, { market, prompt: lower });
          if (leagueInfo) {
            league = leagueInfo.league;
            sport = leagueInfo.sport;
            // Find the team that matches this sport
            const matchingTeam = cityTeams.find(t => TEAM_TO_LEAGUE[t]?.sport === sport);
            if (matchingTeam) {
              team = matchingTeam;
            }
            console.log(`[Parser] 🏟️ City "${city}" → "${team}" → ${league.toUpperCase()} (${sport})`);
          }
        }
        break;
      }
    }
  }
  
  // 🆕 Also detect league from explicit keywords in query (e.g., "nhl avalanche", "nfl jets")
  if (!league) {
    const leagueKeywordMap = {
      'nba': { league: 'nba', sport: 'basketball' },
      'wnba': { league: 'wnba', sport: 'basketball' },
      'nfl': { league: 'nfl', sport: 'football' },
      'nhl': { league: 'nhl', sport: 'hockey' },
      'mlb': { league: 'mlb', sport: 'baseball' },
      'mls': { league: 'mls', sport: 'soccer' },
      'ncaab': { league: 'ncaab', sport: 'basketball' },
      'ncaaf': { league: 'ncaaf', sport: 'football' },
      'college basketball': { league: 'ncaab', sport: 'basketball' },
      'college football': { league: 'ncaaf', sport: 'football' },
      'hockey': { league: 'nhl', sport: 'hockey' },
      'baseball': { league: 'mlb', sport: 'baseball' },
      'soccer': { league: 'mls', sport: 'soccer' },
      'football': { league: 'nfl', sport: 'football' }, // Default football to NFL
    };
    
    for (const [keyword, info] of Object.entries(leagueKeywordMap)) {
      if (new RegExp(`\\b${keyword}\\b`, 'i').test(lower)) {
        league = info.league;
        sport = info.sport;
        console.log(`[Parser] 🏟️ League keyword "${keyword}" detected → ${league.toUpperCase()}`);
        break;
      }
    }
  }
  
  // Player detection - only if NOT a team query and NOT a team market
  // (Don't detect player if we already found a team OR if query is team-focused like "moneyline", "spread")
  if (!team && !['moneyline', 'spread', 'total'].includes(market)) {
    const words = prompt.split(/\s+/);
    const capitalizedWords = [];
    
    // First, try to detect capitalized names (original approach)
    for (let i = 0; i < words.length; i++) {
      const word = words[i];
      // Check if word starts with capital and has at least 3 chars
      if (/^[A-Z][a-z]{2,}/.test(word) && word.length >= 3) {
        // Exclude common words AND market keywords
        const excludeWords = ['Over', 'Under', 'Today', 'Tonight', 'Tomorrow', 'Game', 'Best', 'Good', 'Great', 
                               'Moneyline', 'Spread', 'Total', 'Points', 'Assists', 'Rebounds'];
        if (!excludeWords.includes(word)) {
          capitalizedWords.push(word);
        }
      }
    }
    
    // If we have 1-3 capitalized words, likely a player name
    if (capitalizedWords.length >= 1 && capitalizedWords.length <= 3) {
      player = capitalizedWords.join(' ');
      queryType = 'player';
    }
    
    // 🔧 FIX: Also detect player names from LOWERCASE queries
    // If query has a player prop market (points, assists, etc.) and a line, extract the name part
    if (!player && market && line !== null) {
      const playerPropMarkets = ['points', 'assists', 'rebounds', 'threes', 'blocks', 'steals', 'turnovers',
                                  'points+assists', 'points+rebounds', 'points+assists+rebounds', 'assists+rebounds',
                                  'pass-yards', 'rush-yards', 'rec-yards', 'receptions', 'touchdowns',
                                  'goals', 'saves', 'shots-on-goal', 'double-double', 'triple-double'];
      
      if (playerPropMarkets.includes(market)) {
        // Remove market keywords, numbers, and direction keywords to extract player name
        let namePart = lower
          .replace(/\b(points?|pts?|assists?|ast|rebounds?|reb|threes?|3s|blocks?|steals?|turnovers?|over|under|o\/u|\d+\.?\d*|\+|-)\b/gi, '')
          .replace(/\b(pass(ing)?|rush(ing)?|rec(eiving)?|yards?|yds?|touchdowns?|tds?|receptions?|goals?|saves?|shots?)\b/gi, '')
          .replace(/\s+/g, ' ')
          .trim();
        
        // If we have 1-3 words remaining, it's likely the player name
        const nameWords = namePart.split(/\s+/).filter(w => w.length >= 2);
        if (nameWords.length >= 1 && nameWords.length <= 4) {
          // Capitalize each word for consistent formatting
          player = nameWords.map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
          queryType = 'player';
        }
      }
    }
  }
  
  return {
    queryType,  // 'player', 'team', or 'generic'
    player,
    team,
    market,
    line,
    direction,
    league,     // 🆕 Detected league (e.g., 'nhl', 'nba', 'nfl')
    sport,      // 🆕 Detected sport (e.g., 'hockey', 'basketball', 'football')
    originalPrompt: prompt
  };
}

/**
 * Query with cache-first strategy and automatic fallback to live API
 * @param {string} prompt - Natural language query
 * @param {Object} options - Query options
 * @param {Function} onCacheStatus - Callback for cache status updates
 * @returns {Promise<Object>} - Query results
 */
export async function queryWithCacheFallback(prompt, options = {}, onCacheStatus = null) {
  const {
    league,
    sport,
    team,
    player,
    market,
    prefer = 'straight',
    defaults = {},
    maxAgeMinutes = 60
  } = options;
  
  console.log('[CacheService] Starting cache-first query:', { prompt, options });
  
  // 🧠 Step 0: Parse query to extract entities
  const entities = parseQueryEntities(prompt);
  console.log('[CacheService] 🧠 Parsed entities:', entities);
  
  // Step 1: Try cache first using smart routing
  if (onCacheStatus) onCacheStatus({ 
    stage: 'checking_cache', 
    message: 'Checking cached data...' 
  });
  
  let cacheResult = null;
  
  // 🔧 FIX: Don't treat player prop queries as SGP queries even when prefer='sgpx'
  // If the user is searching for a specific player with a market/line, it's a straight bet, not an SGP
  const isPlayerPropQuery = entities.queryType === 'player' && entities.market && entities.line !== null;
  
  if (isPlayerPropQuery) {
    console.log('[CacheService] 🎯 Player prop query detected - treating as straight bet, not SGP:', {
      player: entities.player,
      market: entities.market,
      line: entities.line
    });
  }
  
  // For SGP queries, check SGP cache first, then odds cache
  // BUT skip SGP path if it's clearly a player prop query
  if ((prefer === 'sgp' || prefer === 'sgpx') && !isPlayerPropQuery) {
    console.log('[CacheService] SGP query detected - checking SGP cache...');
    
    // Try to intelligently extract fixture info from prompt
    // This is a simple heuristic - the NL parser would do better
    const fixturesResult = await queryCachedFixtures({
      league,
      sport,
      team,
      limit: 20,
      maxAgeMinutes
    });
    
    if (fixturesResult.cached && fixturesResult.fixtures?.length > 0) {
      console.log(`[CacheService] Found ${fixturesResult.fixtures.length} cached fixtures for SGP query`);
      
      // ⚡ NOTE: We skip direct Firestore SGP cache queries here because:
      // 1. Frontend doesn't have permission to read sgp_cache collection directly
      // 2. The HTTP function queryCachedSGPs is already called earlier in SgpFlowScreen
      // 3. If we got here, it means the HTTP function returned 0 SGPs, so no point re-checking
      console.log('[CacheService] Skipping direct SGP cache check (use HTTP function instead)');
      console.log('[CacheService] Fixtures found but SGP cache already checked via HTTP - will fall back to API');
    }
  } else {
    // For straight queries, use smart entity-based cache search
    console.log('[CacheService] Straight bet query - checking odds cache...');
    
    // ✅ Use the market already parsed by parseQueryEntities (more reliable)
    // Only extract from prompt if parser didn't find a market
    let marketSearch = entities.market || market;
    
    if (!marketSearch) {
      // Fallback: Extract market keywords by removing team/player names from prompt
      // This handles "warriors 3rd quarter team total" → "3rd quarter team total"
      marketSearch = prompt.toLowerCase();
      
      // Remove team name if detected (use FULL query match to avoid partial removal)
      if (entities.team) {
        // Try to match full team name variations (e.g., "Los Angeles Clippers", "LA Clippers", "Clippers")
        const teamVariations = [
          entities.team,
          `los angeles ${entities.team}`,
          `la ${entities.team}`,
          `golden state ${entities.team}`,
          `new york ${entities.team}`
        ];
        for (const variation of teamVariations) {
          const teamPattern = new RegExp(`\\b${variation.replace(/\s+/g, '\\s+')}\\b`, 'gi');
          marketSearch = marketSearch.replace(teamPattern, '').trim();
        }
      }
      
      // Remove player name if detected
      if (entities.player) {
        const playerPattern = new RegExp(`\\b${entities.player.replace(/\s+/g, '\\s+')}\\b`, 'gi');
        marketSearch = marketSearch.replace(playerPattern, '').trim();
      }
      
      // Clean up extra whitespace
      marketSearch = marketSearch.replace(/\s+/g, ' ').trim();
    }
    
    console.log(`[CacheService] 📊 Market search: "${marketSearch}" (from ${entities.market ? 'parser' : 'prompt extraction'})`);
    
    // 🆕 Use league/sport detected from team name mapping
    // No more defaulting to NBA/basketball - let the AI detect unknown leagues
    const detectedLeague = entities.league || league;
    const detectedSport = entities.sport || sport;
    
    const searchParams = {
      league: detectedLeague || undefined,  // No default - let AI detect if unknown
      sport: detectedSport || undefined,    // No default - let AI detect if unknown
      player: entities.player || player,
      team: entities.team || team,
      market: marketSearch,  // Use parsed or extracted market
      maxAgeMinutes
    };
    
    // Log if we couldn't detect league for a team query
    if (entities.team && !detectedLeague) {
      console.log(`[CacheService] ⚠️ Unknown team "${entities.team}" - no league mapping, AI will detect`);
    }
    
    console.log('[CacheService] 🔍 Search params:', searchParams);
    
    const oddsResult = await getCachedOdds(searchParams);
    
    if (oddsResult.cached && oddsResult.odds?.length > 0) {
      console.log(`[CacheService] ⚡ Odds cache HIT - found ${oddsResult.odds.length} cached odds`);
      
      // 📊 Post-process: Filter by line if specified
      let filteredOdds = oddsResult.odds;
      
      if (entities.line !== null) {
        console.log(`[CacheService] 🎯 Filtering by line: ${entities.direction} ${entities.line}`);
        filteredOdds = oddsResult.odds.filter(odd => {
          const oddLine = odd.selection_line || odd.line || odd.points;
          if (oddLine === null || oddLine === undefined) return false;
          
          const numericLine = parseFloat(oddLine);
          if (isNaN(numericLine)) return false;
          
          // Match lines within 0.5 of target
          return Math.abs(numericLine - entities.line) <= 0.5;
        });
        
        console.log(`[CacheService] 📊 After line filter: ${filteredOdds.length} odds`);
      }
      
      // 🎯 Post-process: Relevance-based sorting
      if (entities.market && filteredOdds.length > 0) {
        console.log(`[CacheService] 📊 Sorting by market relevance: ${entities.market}`);
          const targetMarket = entities.market.toLowerCase();
          
        // Score-based relevance function
        const getMarketRelevanceScore = (odd) => {
          const market = (odd.market || odd.market_id || '').toLowerCase();
          
          // Score 100: Exact match (e.g., "Moneyline" for moneyline query)
          if (market === targetMarket || market === `player ${targetMarket}`) return 100;
          
          // Score 90: Starts with target market
          if (market.startsWith(targetMarket)) return 90;
          
          // Score 80: Plain version (no qualifiers like "1st half", "1st qt")
          const hasQualifier = /\d|half|quarter|qt|qtr|period|inning/i.test(market);
          if (market.includes(targetMarket) && !hasQualifier) return 80;
          
          // Score 50: Contains target market with qualifiers
          if (market.includes(targetMarket)) return 50;
          
          // Score 0: Doesn't match
          return 0;
        };
        
        filteredOdds = filteredOdds.sort((a, b) => {
          const aRelevance = getMarketRelevanceScore(a);
          const bRelevance = getMarketRelevanceScore(b);
          
          // Primary: Sort by relevance (higher first)
          if (aRelevance !== bRelevance) {
            return bRelevance - aRelevance;
          }
          
          // Secondary: Sort by EV% (higher first)
          const aEV = a.ev_percent || 0;
          const bEV = b.ev_percent || 0;
          return bEV - aEV;
        });
        
        console.log(`[CacheService] ✅ Sorted by relevance (exact:100 → starts:90 → plain:80 → qualified:50 → EV%)`);
      }
      
      if (filteredOdds.length > 0) {
        cacheResult = {
          cached: true,
          source: 'odds_cache',
          odds: filteredOdds,
          entities // Include parsed entities for client-side use
        };
      } else {
        console.log('[CacheService] ⚠️ No odds matched filter criteria');
      }
    }
  }
  
  // Step 2: If cache hit, return cached data
  if (cacheResult && cacheResult.cached) {
    if (onCacheStatus) onCacheStatus({ 
      stage: 'cache_hit', 
      message: 'Using cached data' 
    });
    
    console.log('[CacheService] Cache HIT - returning cached data');
    // Transform cached odds into frontend-expected structure
    return {
      success: true,
      cached: true,
      source: cacheResult.source,
      results: [{
        items: cacheResult.odds || [],
        endpoint: 'cache',
        query: { ...entities }
      }],
      entities
    };
  }
  
  // Step 3: Cache miss - fall back to live API
  console.log('[CacheService] Cache MISS - falling back to live API');
  
  if (onCacheStatus) onCacheStatus({ 
    stage: 'cache_miss', 
    message: 'Searching for best odds...' 
  });
  
  try {
    const base = getFunctionsBaseUrl();
    
    // ⚠️ Note: The backend may still return cached data even on frontend "cache miss"
    // Only show "live API" message if backend actually calls OpticOdds API
    
    // 🔧 Override prefer to 'straight' for player prop queries
    const effectivePrefer = isPlayerPropQuery ? 'straight' : prefer;
    
    const body = {
      prompt,
      prefer: effectivePrefer,
      defaults: {
        ...defaults,
        sport,
        league: league?.toLowerCase(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        now: new Date().toISOString()
      }
    };
    
    console.log('[CacheService] Calling /nlOdds with:', body);
    if (isPlayerPropQuery) {
      console.log('[CacheService] 🎯 Overrode prefer from', prefer, 'to', effectivePrefer, 'for player prop query');
    }
    
    const response = await fetch(`${base}/nlOdds`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    
    const text = await response.text();
    
    if (!response.ok) {
      throw new Error(`API Error: ${text}`);
    }
    
    const data = JSON.parse(text);
    
    // Check if backend returned cached data or had to hit live API
    const wasBackendCached = data.cached === true || data.fromCache === true;
    
    if (onCacheStatus) onCacheStatus({ 
      stage: wasBackendCached ? 'backend_cache_hit' : 'live_api_complete', 
      message: wasBackendCached ? 'Found data' : 'Search complete' 
    });
    
    console.log('[CacheService] nlOdds returned:', wasBackendCached ? 'BACKEND CACHE' : 'LIVE API');
    
    return {
      success: true,
      cached: wasBackendCached,
      ...data
    };
    
  } catch (error) {
    console.error('[CacheService] Live API error:', error);
    
    if (onCacheStatus) onCacheStatus({ 
      stage: 'error', 
      message: `Error: ${error.message}` 
    });
    
    throw error;
  }
}

/**
 * Check if cache is healthy and available
 * @returns {Promise<{healthy: boolean, age_minutes: number}>}
 */
export async function checkCacheHealth() {
  try {
    const stats = await getCacheStats();
    
    const isHealthy = stats.success && 
                     stats.cache_status === 'active' && 
                     stats.age_minutes < 120; // Cache should be less than 2 hours old
    
    return {
      healthy: isHealthy,
      age_minutes: stats.age_minutes || 999,
      stats
    };
  } catch (error) {
    return {
      healthy: false,
      age_minutes: 999,
      error: error.message
    };
  }
}

/**
 * ⚡ Query SGP cache for pre-generated SGPs
 * @param {string} fixtureId - Fixture ID to look up
 * @returns {Promise<{success: boolean, cached: boolean, sgps: Object, fixture: Object}>}
 */
export async function querySGPCache(fixtureId) {
  try {
    console.log('[CacheService] Checking SGP cache for fixture:', fixtureId);
    
    // Import Firebase here to avoid circular dependencies
    const { db } = await import('../firebaseConfig');
    const { doc, getDoc } = await import('firebase/firestore');
    
    const sgpDoc = await getDoc(doc(db, 'sgp_cache', fixtureId));
    
    if (!sgpDoc.exists()) {
      console.log('[CacheService] No SGP cache found for fixture');
      return {
        success: true,
        cached: false,
        sgps: null,
        message: 'No cached SGPs for this fixture'
      };
    }
    
    const data = sgpDoc.data();
    
    // Check if cache is expired
    const generatedAt = data.generated_at?.toDate?.() || new Date(data.generated_at);
    const expiresAt = data.expires_at?.toDate?.() || new Date(data.expires_at);
    const now = new Date();
    
    if (now > expiresAt) {
      console.log('[CacheService] SGP cache expired');
      return {
        success: true,
        cached: false,
        sgps: null,
        message: 'SGP cache expired'
      };
    }
    
    const cacheAgeMinutes = (now - generatedAt) / (1000 * 60);
    console.log(`[CacheService] SGP cache HIT (age: ${cacheAgeMinutes.toFixed(1)} mins)`);
    
    return {
      success: true,
      cached: true,
      sgps: data.sgps || {},
      stats: data.stats || {},
      fixture: {
        id: data.fixture_id,
        home_team: data.home_team,
        away_team: data.away_team,
        league: data.league,
        sport: data.sport,
        start_date: data.start_date
      },
      cache_age_minutes: cacheAgeMinutes
    };
  } catch (error) {
    console.error('[CacheService] Error querying SGP cache:', error);
    return {
      success: false,
      cached: false,
      sgps: null,
      error: error.message
    };
  }
}

/**
 * 🚀 Generate SGPs on-demand for a fixture
 * @param {string} fixtureId - Fixture ID
 * @param {Object} cachedOdds - Pre-fetched cached odds (optional)
 * @param {Function} onProgress - Progress callback (optional)
 * @returns {Promise<{success: boolean, sgps: Object, stats: Object}>}
 */
export async function generateSGPsOnDemand(fixtureId, cachedOdds = null, onProgress = null) {
  try {
    console.log('[CacheService] Generating SGPs on-demand for:', fixtureId);
    
    if (onProgress) onProgress({ stage: 'generating', message: 'Generating custom SGPs...' });
    
    const base = getFunctionsBaseUrl();
    const url = `${base}/generateSGPs`;
    
    const body = {
      fixture_id: fixtureId,
      cached_odds: cachedOdds, // Pass cached odds if available
      options: {
        maxCombinations: 500 // Use 500 limit for manual generation (fast)
      }
    };
    
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'SGP generation failed');
    }
    
    if (onProgress) onProgress({ stage: 'complete', message: 'SGPs generated!' });
    
    console.log('[CacheService] SGPs generated successfully:', {
      twoLeg: data.stats?.['2-leg']?.total || 0,
      threeLeg: data.stats?.['3-leg']?.total || 0
    });
    
    return {
      success: true,
      sgps: data.sgps || {},
      stats: data.stats || {},
      fixture: data.fixture || {}
    };
  } catch (error) {
    console.error('[CacheService] Error generating SGPs:', error);
    
    if (onProgress) onProgress({ stage: 'error', message: `Error: ${error.message}` });
    
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * ⚡ Smart SGP query with cache-first + on-demand generation
 * 
 * Flow:
 * 1. Check SGP cache → if found, return immediately ✅
 * 2. Check if fixture is in odds cache with enough markets → generate on-demand ✅
 * 3. Fall back to live API ⚠️
 * 
 * @param {Object} params - Query parameters
 * @param {string} params.fixtureId - Fixture ID (if known)
 * @param {string} params.prompt - Natural language query
 * @param {Object} params.options - Query options
 * @param {Function} onStatus - Status callback
 * @returns {Promise<Object>}
 */
export async function querySGPsWithCacheFallback(params, onStatus = null) {
  const { fixtureId, prompt, options = {} } = params;
  
  console.log('[CacheService] Smart SGP query:', params);
  
  // Step 1: If we have a fixture ID, check SGP cache directly
  if (fixtureId) {
    if (onStatus) onStatus({ stage: 'checking_sgp_cache', message: 'Checking for pre-generated SGPs...' });
    
    const sgpResult = await querySGPCache(fixtureId);
    
    if (sgpResult.cached && sgpResult.sgps) {
      console.log('[CacheService] SGP cache HIT!');
      if (onStatus) onStatus({ stage: 'sgp_cache_hit', message: 'Found pre-generated SGPs!' });
      
      return {
        success: true,
        cached: true,
        source: 'sgp_cache',
        sgps: sgpResult.sgps,
        stats: sgpResult.stats,
        fixture: sgpResult.fixture
      };
    }
    
    // Step 2: SGP not cached - check if we can generate on-demand
    console.log('[CacheService] SGP not cached, checking if generatable...');
    if (onStatus) onStatus({ stage: 'checking_generatable', message: 'Checking if SGPs can be generated...' });
    
    // Import Firebase here
    const { db } = await import('../firebaseConfig');
    const { doc, getDoc } = await import('firebase/firestore');
    
    const oddsDoc = await getDoc(doc(db, 'odds_cache', fixtureId));
    
    if (oddsDoc.exists()) {
      const oddsData = oddsDoc.data();
      const totalMarkets = oddsData.total_markets || 0;
      
      if (totalMarkets >= 10) {
        // Fixture has enough markets - generate SGPs on-demand!
        console.log(`[CacheService] Fixture is generatable (${totalMarkets} markets) - generating now...`);
        if (onStatus) onStatus({ stage: 'generating_sgps', message: `Generating SGPs from ${totalMarkets} markets...` });
        
        const genResult = await generateSGPsOnDemand(
          fixtureId,
          oddsData.featured_markets || oddsData.markets,
          onStatus
        );
        
        if (genResult.success) {
          return {
            success: true,
            cached: false,
            generated: true,
            source: 'on_demand_generation',
            sgps: genResult.sgps,
            stats: genResult.stats,
            fixture: genResult.fixture
          };
        }
      } else {
        console.log(`[CacheService] Fixture not generatable (only ${totalMarkets} markets, need 10+)`);
      }
    }
  }
  
  // Step 3: Fall back to live API query
  console.log('[CacheService] Falling back to live API for SGP query');
  if (onStatus) onStatus({ stage: 'live_api', message: 'Performing live search...' });
  
  return await queryWithCacheFallback(prompt, options, onStatus);
}

